import { useDebounce } from "../../../../hooks/use-debounce";
import { FilterBuilder } from "../../../../components/filter/FilterBuilder";
import { FilterRecord } from "../../../../components/filter/filter-constant";
import useActiveComponentCountController from "../../../../components/filter/use-ActiveComponentCountController";
import { useAppointmentTypeGetAll } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { useProviderListV2 } from "../../../../queries/use-provider-list-v2";
import { DEF_PAGINATION, getXtenantId } from "../../../../utils/utils";
import { NEW_DATE_RANGE_FOR_CLAIMS_TYPE } from "./claims/claims-constants";
import { ADDENDUM_SIGNED_LIST, BILLED_STATUS_LIST } from "./billing-constant";
import { Option } from "../../../../constants/common";

interface PatientFilterProps {
  onApply: (v: FilterData) => void;
  filters: FilterData;
  setFilters: (v: FilterData) => void;
}

export type FilterData = {
  date_range: undefined;
  startDate: undefined;
  endDate: undefined;
  provider_Name?: { value: string; label: string };
  appointment_Type?: { value: string; label: string };
  billed_Status?: Option[];
  addendum_Signed?: Option;
};

const BillingFilter = ({
  onApply,
  filters,
  setFilters,
}: PatientFilterProps) => {
  const handleApply = (v: FilterData) => {
    setFilters(v);
    onApply(v);
  };

  const [searchProviderGroupKey, setSearchProviderGroupKey] =
    useDebounce<string>("");
  const { data: providersList } = useProviderListV2({
    pagination: DEF_PAGINATION,
    searchString: searchProviderGroupKey,
    xTENANTID: getXtenantId(),
    status: true,
    archive: false,
  });

  const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
    useDebounce<string>("");
  const { data: appointmentTypeList } = useAppointmentTypeGetAll({
    searchString: searchAppointmentTypeKey,
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  const activeFilterCount = useActiveComponentCountController(filters);

  const filtersProvider: FilterRecord = {
    provider_Name: {
      type: "select",
      label: "Provider Name",
      options: providersList?.data?.content?.map((it: any) => ({
        value: it?.uuid,
        label: it?.name,
      })),
      placeholder: "Search Provider",
      onSearch: (val: string) => setSearchProviderGroupKey(val),
    },
    appointment_Type: {
      type: "select",
      label: "Appointment Type",
      // The visit list API filters on the appointment type key, so the option
      // keeps sending `typeName` while showing the configured `nameString`.
      options: appointmentTypeList?.data?.content
        ?.filter((it: any) => it?.typeName)
        ?.map((it: any) => ({
          value: it?.typeName,
          label: it?.nameString ?? it?.typeName,
        })),
      placeholder: "Select Appointment Type",
      onSearch: (val: string) => setSearchAppointmentTypeKey(val),
    },
    billed_Status: {
      type: "multi_select",
      label: "Billed Status",
      options: BILLED_STATUS_LIST,
      placeholder: "Select Billed Status",
    },
    addendum_Signed: {
      type: "select",
      label: "Addendum Signed",
      options: ADDENDUM_SIGNED_LIST,
      placeholder: "Select Addendum Signed",
    },
    date_range: {
      type: "date_range_preset",
      label: "Date Range",
      childKeys: ["date_range", "startDate", "endDate"],
      options: NEW_DATE_RANGE_FOR_CLAIMS_TYPE,
    },
  };

  return (
    <FilterBuilder
      filters={filtersProvider}
      onApply={handleApply}
      prefillValues={filters as any}
      activeFilterCount={activeFilterCount}
      h="10rem"
    />
  );
};

export default BillingFilter;
